import { Component } from '@theme/component';

/**
 * Scroll-synced progress indicator for the Ancestral Stitch customization
 * flow. Watches `[data-customization-step]` markers placed elsewhere in the
 * product-details column (they are not descendants of this element) and
 * updates its own label/dots to reflect whichever step is nearest the
 * vertical center of the viewport.
 *
 * Degrades gracefully: if no step markers are found on the page, the rail
 * hides itself rather than showing a misleading "Step 1 of N".
 */
class CustomizationProgressRail extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {HTMLElement[]} */
  #steps = [];

  connectedCallback() {
    super.connectedCallback();

    this.#steps = Array.from(document.querySelectorAll('[data-customization-step]'));

    if (!this.#steps.length) {
      this.hidden = true;
      return;
    }

    const dots = this.refs['rail-dots'];
    const totalSteps = Number(this.getAttribute('total-steps')) || this.#steps.length;

    if (Array.isArray(dots)) {
      dots.forEach((dot, index) => {
        dot.hidden = index >= totalSteps;
      });
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];

        if (!visible) return;
        this.#setActiveStep(/** @type {HTMLElement} */ (visible.target));
      },
      { rootMargin: '-40% 0px -50% 0px', threshold: 0 }
    );

    for (const step of this.#steps) {
      this.#observer.observe(step);
    }

    this.#setActiveStep(this.#steps[0]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  /**
   * @param {HTMLElement} step
   */
  #setActiveStep(step) {
    const index = Number(step.dataset.stepIndex || '1');
    const label = step.dataset.stepLabel || '';

    const labelRef = this.refs['rail-label'];
    if (labelRef instanceof HTMLElement) {
      labelRef.textContent = `Step ${index} of ${this.#steps.length} — ${label}`;
    }

    const dots = this.refs['rail-dots'];
    if (Array.isArray(dots)) {
      for (const [dotIndex, dot] of dots.entries()) {
        dot.classList.toggle('is-active', dotIndex + 1 === index);
        dot.classList.toggle('is-complete', dotIndex + 1 < index);
      }
    }
  }
}

if (!customElements.get('customization-progress-rail')) {
  customElements.define('customization-progress-rail', CustomizationProgressRail);
}
